import * as React from 'react';
import { Form } from 'react-bootstrap';
import { Link } from 'react-router';
import { Dropdown, useCacheQuery } from 'gossamer-front-end';
import { FeedbackForm } from 'cb-model';
import { DEFAULT_FEEDBACK_FORM_ID, PERSONAL_INSTITUTION_ID } from 'cb-unified';
import { EventFlowOptions } from 'cb-model/SharedOptions';
import { EventDetailsSectionLayout } from './EventDetailsSectionLayout';
import { EventDetails, EventErrors, HandleEventDetailsChange, ValidateEventFormField } from './eventDetailsFormTypes';

type Props<T extends EventDetails> = {
  eventDetails: T | undefined;
  handleDetailsChange: HandleEventDetailsChange<T>;
  errors: EventErrors;
  setErrors: React.Dispatch<React.SetStateAction<EventErrors>>;
  institutionId: number;
  validateForm: ValidateEventFormField<T>;
};

const getFlowOptionLabel = (flowOption: string | null | undefined): string => {
  if (!flowOption) return 'Not set';
  const option = EventFlowOptions.find((o) => o.id === flowOption);
  return option ? option.label : flowOption;
};

export function EventDuringAfterSection<T extends EventDetails>(props: Props<T>): React.JSX.Element {
  const { eventDetails, handleDetailsChange, errors, setErrors, institutionId, validateForm } = props;

  const isPersonalInstitution = institutionId === PERSONAL_INSTITUTION_ID;
  const flowOption = eventDetails?.flow_option as string | null | undefined;

  const feedbackFormQuery = useCacheQuery(
    FeedbackForm,
    {
      where: {
        or: [
          { fieldId: 'id', operator: 'eq', value: DEFAULT_FEEDBACK_FORM_ID },
          { fieldId: 'institution_id', operator: 'eq', value: institutionId },
        ],
      },
      limit: 100,
    },
    isPersonalInstitution,
  );

  const feedbackOptions = React.useMemo(
    () => (feedbackFormQuery.data || []).map((f: any) => ({ id: String(f.id), label: f.title })),
    [feedbackFormQuery.data],
  );

  return (
    <EventDetailsSectionLayout
      title="During and After the Event"
      subtitle="Settings derived from the selected Event Profile that control what happens during and after the event."
    >
      <Form.Group>
        <Form.Label>What is the attendee prompted to do during and after the event happens?</Form.Label>
        <div>
          <b>{getFlowOptionLabel(flowOption)}</b>
        </div>
        <Form.Text muted>This is set by the Event Profile selected above.</Form.Text>
      </Form.Group>

      {!isPersonalInstitution && ['noAttendance', 'full'].includes(flowOption) && feedbackOptions.length > 0 && (
        <Form.Group controlId="feedback-form">
          <Form.Label>Feedback Form*</Form.Label>
          <Dropdown
            mandatory={true}
            onItemSelected={(value) => {
              const parsed = parseInt(value, 10);
              handleDetailsChange('feedback_form_id' as keyof T, Number.isFinite(parsed) ? parsed : null);
              validateForm({ eventDetails, setErrors, key: 'feedback_form_id' as keyof T });
            }}
            options={feedbackOptions}
            validity={errors.feedback_form_id ? false : undefined}
            value={String(eventDetails?.feedback_form_id || '')}
          />
          {errors.feedback_form_id && <Form.Text className="text-danger">{errors.feedback_form_id}</Form.Text>}
        </Form.Group>
      )}

      {!isPersonalInstitution && flowOption === 'full' && (
        <div style={{ fontSize: '0.875rem' }}>
          Feedback forms determine which questions are asked after the event. You can use the Default Form provided by
          Careerbook, or set up your own. <Link to="/admin/feedback-form/search">Manage your feedback forms</Link>.
        </div>
      )}
    </EventDetailsSectionLayout>
  );
}
